import React, { useEffect, useState, useCallback, useRef } from "react";
import styled from "styled-components";
import * as nifti from 'nifti-reader-js';
import NiftiFileReader from "@pages/Operations/DataLabellingPage/NiftiFileReader";
import { useParams } from "react-router-dom";
import { getInstanceALAnnotations, saveALCorrectedAnnotations, getALCorrectionData } from "@services/dashboardService";
import { showErrorSnackbar, showSuccessSnacbar } from "@components/shared-layouts/Snackbar/helper";
import { Button, CircularProgress } from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import {
  convert_to_nifit_file_reader_data,
  labelsFromContextState,
  type ProjectLabel,
} from "@pages/Operations/DataLabellingPage/helpers";
import service from "@services/index";

const Navbar = styled.div`
  height: 75px;
  background: #f04a23;
  display: flex;
  align-items: center;
  padding: 0 24px;
  box-shadow: 0px 3px 3px -2px rgba(0,0,0,0.2), 
              0px 3px 4px 0px rgba(0,0,0,0.14), 
              0px 1px 8px 0px rgba(0,0,0,0.12);
  justify-content: space-between;
`;

const NavbarTitle = styled.h1`
  color: white;
  font-size: 20px;
  font-weight: 900;
  margin: 0;
  text-align: center;
  flex: 1;
`;

const ContentWrapper = styled.div`
  display: flex;
  height: calc(100vh - 75px);
  width: 100%;
`;

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 100%;
`;

const Sidebar = styled.div`
  width: 300px;
  border-right: 1px solid #e0e0e0;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  box-shadow: 2px 0 4px rgba(0, 0, 0, 0.05);
`;

const SidebarHeader = styled.div`
  padding: 16px;
  border-bottom: 1px solid #e0e0e0;
  background: #f04a23;
`;

const SidebarTitle = styled.h3`
  margin: 0;
  color: white;
  font-size: 15px;
  font-weight: 800;
  text-align: center;
`;

const ImageListContainer = styled.div`
  padding: 16px;
  height: calc(100vh - 145px);
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;
    
    &:hover {
      background: #a8a8a8;
    }
  }
`;

const ImageList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ImageListItem = styled.li<{ isSelected: boolean }>`
  padding: 12px 16px;
  cursor: pointer;
  border-radius: 8px;
  background: ${props => props.isSelected ? 'linear-gradient(to right, #f04a23, #ed8623a1)' : '#ffffff'};
  border: 1px solid ${props => props.isSelected ? '#f04a23' : '#e0e0e0'};
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 12px;

  &:hover {
    background: ${props => props.isSelected ? 'linear-gradient(to right, #f04a23, #ed8623a1)' : '#f5f5f5'};
    border-color: #f04a23;
  }

  &::before {
    content: '';
    display: block;
    width: 24px;
    height: 24px;
    background: ${props => props.isSelected ? 'white' : '#f04a23'};
    mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z'/%3E%3C/svg%3E") center/contain no-repeat;
    opacity: ${props => props.isSelected ? '1' : '0.8'};
  }
`;

const ImageName = styled.span<{ isSelected: boolean }>`
  font-size: 13px;
  color: ${props => props.isSelected ? 'white' : '#333'};
  font-weight: ${props => props.isSelected ? 'bold' : 'normal'};
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SliceInfo = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  gap: 8px;
`;

const MainContent = styled.div`
  flex: 1;
  display: flex;
  width: 100%;
`;

const NiftiViewerContainer = styled.div`
  flex: 1;
  width: 100%;
  height: 100%;
`;

const ProgressContainer = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  gap: 10px;
`;

const ProgressWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  max-width: 300px;
  width: 100%;
`;

const ProgressText = styled.div`
  color: #f04a23;
  font-weight: 500;
  font-size: 14px;
`;

const ProgressBarContainer = styled.div`
  width: 300px;
  height: 20px;
  border: 1px solid #ed8623;
  border-radius: 10px;
  overflow: hidden;
  background-color: #fff;
`;

const ProgressBar = styled.div<{ progress: number }>`
  width: ${props => `${props.progress}%`};
  height: 100%;
  background: linear-gradient(to right, #f04a23, #ed8623);
  transition: width 0.3s ease-in-out;
`;

const WelcomeContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 20px;
  text-align: center;
  color: #666;
`;

const WelcomeIcon = styled.div`
  font-size: 48px;
  color: #f04a23;
  margin-bottom: 20px;
  
  &::before {
    content: '';
    display: block;
    width: 64px;
    height: 64px;
    background: #f04a23;
    mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z'/%3E%3C/svg%3E") center/contain no-repeat;
  }
`;

const WelcomeTitle = styled.h2`
  color: #f04a23;
  margin-bottom: 10px;
  font-size: 24px;
`;

const WelcomeText = styled.p`
  font-size: 16px;
  max-width: 500px;
  line-height: 1.6;
`;

// Utility function to format bytes to human-readable format
const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

/**
 * Sort ranges by their start value.
 * Example:
 * Input: [{ start: 5, end: 10 }, { start: 1, end: 3 }]
 * Output: [{ start: 1, end: 3 }, { start: 5, end: 10 }]
 */
const sortRanges = (ranges: SliceRange[]): SliceRange[] => {
  return [...ranges].sort((a, b) => a.start - b.start);
};

/**
 * Merge overlapping or adjacent ranges.
 * Example:
 * Input: [{ start: 1, end: 5 }, { start: 4, end: 8 }, { start: 10, end: 12 }]
 * Output: [{ start: 1, end: 8 }, { start: 10, end: 12 }]
 */
const mergeRanges = (ranges: SliceRange[]): SliceRange[] => {
  if (!ranges || ranges.length === 0) {
    return [];
  }

  const sortedRanges = sortRanges(ranges);
  const merged: SliceRange[] = [{ ...sortedRanges[0] }];

  for (let i = 1; i < sortedRanges.length; i++) {
    const current = sortedRanges[i];
    const last = merged[merged.length - 1];

    // Overlap or adjacency check
    if (current.start <= last.end + 1) {
      last.end = Math.max(last.end, current.end);
    } else {
      merged.push({ ...current });
    }
  }

  return merged;
};

interface SegmentationData {
  [sliceIndex: number]: {
    polygons: Array<{
      points: Array<{ x: number; y: number }>;
      label: string;
      color: string;
    }>;
    activePolygon: Array<{ x: number; y: number }>;
  };
}

interface ImageDimensions {
  width: number;
  height: number;
  totalSlices: number;
}

interface SliceRange {
  start: number;
  end: number;
}

interface ALCorrectionSets {
  hard: SliceRange[];
  med: SliceRange[];
  easy: SliceRange[];
  custom: SliceRange[];
}

interface ALCorrectionData {
  message: string;
  instances: {
    [key: string]: {
      sets: ALCorrectionSets;
    };
  };
  classes: string[];
  keys: any[];
  colors: string[];
  slice_interval?: number;
}

function ALCorrectionDetailPage() {
  const { projectId, fileName } = useParams<{ projectId: string; fileName: string; project_type: string }>();
  
  const [niftiFile, setNiftiFile] = useState<ArrayBuffer | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [downloadLoaded, setDownloadLoaded] = useState<number>(0);
  const [downloadTotal, setDownloadTotal] = useState<number>(0);
  const [preLoadedAnnotations, setPreLoadedAnnotations] = useState<any>(null);
  const preLoadedAnnotationsRef = useRef<any>(null);
  const [imageSize, setImageSize] = useState<number[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [loadingAnnotations, setLoadingAnnotations] = useState(false);
  const [totalSlices, setTotalSlices] = useState<number>(0);
  const [selectedSlice, setSelectedSlice] = useState<number | null>(null);
  const [saveTrigger, setSaveTrigger] = useState<number>(0);
  const niftiCache = useRef<Map<string, ArrayBuffer>>(new Map());
  const [filteredSlices, setFilteredSlices] = useState<number[]>([]);
  const [loadingALData, setLoadingALData] = useState(false);
  const [labels, setLabels] = useState<ProjectLabel[]>([]);
  
  const decodedFileName = fileName ? decodeURIComponent(fileName) : '';

  // Calculate filtered slices from sets
  // Logic based on user examples:
  // - {start: 74, end: 78} with interval 3 → 74, 78
  // - {start: 74, end: 79} with interval 3 → 74, 78
  // - {start: 74, end: 85} with interval 3 → 74, 78, 82
  // Always include start, then add interval values that are <= end
  // Include end if (end - start) % interval === 0 (end is a valid interval point)
  const calculateFilteredSlices = useCallback((sets: ALCorrectionSets, sliceInterval: number): number[] => {
    // Collect all ranges from all difficulty levels into a single array
    const allRanges: SliceRange[] = [
      ...sets.hard,
      ...sets.med,
      ...sets.easy,
      ...sets.custom
    ];
    
    // Merge overlapping or adjacent ranges
    const mergedRanges = mergeRanges(allRanges);
    
    // Process each merged range to extract slices based on interval
    const sliceSet = new Set<number>();
    
    mergedRanges.forEach(range => {
      const { start, end } = range;
      
      // Always include start
      sliceSet.add(start);
      
      // Calculate slices: start + interval, start + 2*interval, etc.
      // Continue while calculated slice is <= end
      let index = 1;
      let calculatedSlice = start + (index * sliceInterval);
      
      while (calculatedSlice <= end) {
        sliceSet.add(calculatedSlice);
        index++;
        calculatedSlice = start + (index * sliceInterval);
      }
      
      // Include end if it's a valid interval point (start + n*interval for some n)
      // Check if (end - start) is a multiple of sliceInterval
      if (end !== start && (end - start) % sliceInterval === 0) {
        sliceSet.add(end);
      }
    });
    
    // Convert to sorted array
    return Array.from(sliceSet).sort((a, b) => a - b);
  }, []);

  // Fetch AL correction data (instances and slice_interval)
  const fetchALCorrectionData = useCallback(async () => {
    if (!projectId) {
      return;
    }

    try {
      setLoadingALData(true);
      const data: ALCorrectionData = await getALCorrectionData(projectId);

      if (data?.classes?.length) {
        const projectLabels = labelsFromContextState(data.classes, data.colors);
        setLabels(projectLabels);
        console.log("Loaded labels:", projectLabels.length);
      }
      
      // Extract sets for current file
      if (data?.instances && decodedFileName && data.instances[decodedFileName]) {
        const fileSets = data.instances[decodedFileName].sets;
        const sliceInterval = data.slice_interval;
        const isValidSliceInterval =
          typeof sliceInterval === 'number' &&
          Number.isFinite(sliceInterval) &&
          sliceInterval > 0 &&
          Number.isInteger(sliceInterval);

        if (!isValidSliceInterval) {
          showErrorSnackbar('slice_interval missing or invalid in API response');
          setFilteredSlices([]);
        } else {
          const calculatedSlices = calculateFilteredSlices(fileSets, sliceInterval);
          setFilteredSlices(calculatedSlices);
        }
      }
    } catch (err: any) {
      console.error('[fetchALCorrectionData] Error fetching AL correction data:', err);
      // Don't show error snackbar here as it might be too noisy
      // The component will still work with all slices if this fails
    } finally {
      setLoadingALData(false);
    }
  }, [projectId, decodedFileName, calculateFilteredSlices]);

  // Load AL annotations from API
  const fetchALAnnotations = useCallback(async () => {
    console.log('[fetchALAnnotations] Function called', { projectId, decodedFileName });
    
    if (!projectId || !decodedFileName) {
      console.warn('[fetchALAnnotations] Early return - missing projectId or decodedFileName', { projectId, decodedFileName });
      return;
    }

    const apiUrl = `https://genie-be-3d-dev.avawatz.com/api/genie-3d/ct-segmentation/operations/clarifier/loop/al-correction/${projectId}/${decodedFileName}`;
    console.log('[fetchALAnnotations] Making API call to:', apiUrl);

    try {
      setLoadingAnnotations(true);
      const data = await getInstanceALAnnotations(projectId, decodedFileName);
      
      console.log('[fetchALAnnotations] API response received', { 
        hasPolygons: !!data?.polygons, 
        hasSize: !!data?.size,
        polygonsKeys: data?.polygons ? Object.keys(data.polygons) : [],
        samplePolygon: data?.polygons ? Object.values(data.polygons)[0] : null
      });
      
      if (data?.polygons) {
        // API returns polygons in the format: { [sliceIndex: string]: [{ points: [...], label: string, color: string }] }
        // This is the same format that NiftiFileReader expects for preLoadedAnnotations
        console.log('[fetchALAnnotations] Setting annotations', { 
          sliceCount: Object.keys(data.polygons).length,
          firstSliceData: data.polygons[Object.keys(data.polygons)[0]]
        });
        // Only update if the data has actually changed to prevent infinite re-renders
        const polygonsString = JSON.stringify(data.polygons);
        if (preLoadedAnnotationsRef.current !== polygonsString) {
          preLoadedAnnotationsRef.current = polygonsString;
          setPreLoadedAnnotations(data.polygons);
          console.log('[fetchALAnnotations] Annotations state updated');
        } else {
          console.log('[fetchALAnnotations] Annotations unchanged, skipping update');
        }
      } else {
        console.warn('[fetchALAnnotations] No polygons in API response', { data });
      }
      
      if (data?.size && Array.isArray(data.size)) {
        setImageSize(data.size);
        console.log('[fetchALAnnotations] Image size set', { size: data.size });
      }
    } catch (err: any) {
      console.error('[fetchALAnnotations] Error fetching AL annotations:', err);
      const errorMessage = err?.error?.message || err?.message || 'Failed to fetch AL annotations';
      showErrorSnackbar(errorMessage);
    } finally {
      setLoadingAnnotations(false);
    }
  }, [projectId, decodedFileName]);

  // Load NIFTI file from API
  const fetchNiftiFile = useCallback(async (imageId: string) => {
    if (!projectId) return;

    try {
      setNiftiFile(null);
      setLoading(true);
      setError(null);
      setDownloadProgress(0);
      setDownloadLoaded(0);
      setDownloadTotal(0);

      // Check cache first
      if (niftiCache.current.has(imageId)) {
        console.log('Loading from cache:', imageId);
        const cachedFile = niftiCache.current.get(imageId)!;
        setNiftiFile(cachedFile);
        setDownloadProgress(100);
        setLoading(false);
        
        // Extract total slices from cached file
        try {
          const niftiHeader = nifti.readHeader(cachedFile);
          if (niftiHeader && niftiHeader.dims) {
            setTotalSlices(niftiHeader.dims[4] || 0);
            // Don't set selectedSlice here - it will be set when filtered slices are calculated
          }
        } catch (e) {
          console.error('Error reading NIFTI header:', e);
        }
        return;
      }

      const response = await service.get(
        `/api/genie-3d/data/${projectId}/image/${imageId}`,
        null,
        null,
        'https://genie-be-3d-dev.avawatz.com',
        { 
          responseType: 'arraybuffer',
          onDownloadProgress: (progressEvent: any) => {
            if (progressEvent.total && progressEvent.total > 0) {
              const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              // Only update if valid and within range
              if (!isNaN(percentCompleted) && isFinite(percentCompleted) && percentCompleted >= 0 && percentCompleted <= 100) {
                setDownloadProgress(percentCompleted);
                setDownloadLoaded(progressEvent.loaded);
                setDownloadTotal(progressEvent.total);
              }
            }
          }
        }
      );

      if (!response.data) {
        throw new Error('No data received from the server');
      }

      console.log('Array Buffer Size:', response.data.byteLength);
      
      // Store in cache
      niftiCache.current.set(imageId, response.data);
      
      setDownloadProgress(100);
      
      // Extract total slices from NIFTI header
      try {
        const niftiHeader = nifti.readHeader(response.data);
        if (niftiHeader && niftiHeader.dims) {
          setTotalSlices(niftiHeader.dims[4] || 0);
          // Don't set selectedSlice here - it will be set when filtered slices are calculated
        }
      } catch (e) {
        console.error('Error reading NIFTI header:', e);
      }
      
      setNiftiFile(response.data);

    } catch (err) {
      console.error('Error fetching NIFTI file:', err);
      setError(err instanceof Error ? err.message : 'Failed to load NIFTI file.');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  // Update selected slice when filtered slices change
  useEffect(() => {
    if (filteredSlices.length > 0) {
      if (selectedSlice === null) {
        // Set default selected slice to first filtered slice
        setSelectedSlice(filteredSlices[0]);
      } else {
        // If a slice is already selected, verify it's in the filtered list
        const isSelectedSliceValid = filteredSlices.includes(selectedSlice);
        if (!isSelectedSliceValid) {
          // If selected slice is not in filtered list, select first filtered slice
          setSelectedSlice(filteredSlices[0]);
        }
      }
    }
  }, [filteredSlices, selectedSlice]);

  // Load file and annotations on mount
  useEffect(() => {
    console.log('[useEffect] Component mounted or dependencies changed', { projectId, decodedFileName });
    
    if (!projectId || !decodedFileName) {
      console.warn('[useEffect] Missing required params, skipping API calls', { projectId, decodedFileName });
      return;
    }

    console.log('[useEffect] Calling fetchNiftiFile, fetchALAnnotations, and fetchALCorrectionData');
    fetchNiftiFile(decodedFileName);
    fetchALAnnotations();
    fetchALCorrectionData();
  }, [projectId, decodedFileName, fetchNiftiFile, fetchALAnnotations, fetchALCorrectionData]);

  // Custom save handler for AL Correction
  const handleSave = useCallback(async (segmentationData: SegmentationData, imageDimensions: ImageDimensions | null) => {
    if (!projectId || !decodedFileName) {
      showErrorSnackbar('Project ID or file name is missing');
      return;
    }

    try {
      setSaving(true);
      
      // Convert segmentationData to API format
      const convertedData = convert_to_nifit_file_reader_data(segmentationData as any);
      
      // Use size from API response if available, otherwise from imageDimensions
      const size = imageSize || (imageDimensions ? [imageDimensions.height, imageDimensions.width, imageDimensions.totalSlices] : [512, 512, 0]);
      
      await saveALCorrectedAnnotations(projectId, decodedFileName, size, convertedData);
      showSuccessSnacbar('AL-corrected annotations saved successfully');
    } catch (err: any) {
      console.error('Error saving AL-corrected annotations:', err);
      const errorMessage = err?.error?.message || 'Failed to save AL-corrected annotations';
      showErrorSnackbar(errorMessage);
      throw err; // Re-throw so NiftiFileReader can handle it
    } finally {
      setSaving(false);
    }
  }, [projectId, decodedFileName, imageSize]);

  return (
    <PageContainer>
      <Navbar>
        <NavbarTitle>AL Correction - {decodedFileName}</NavbarTitle>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={() => {
            setSaveTrigger(prev => prev + 1);
          }}
          disabled={saving || !niftiFile}
          sx={{
            backgroundColor: 'white',
            color: '#f04a23',
            '&:hover': {
              backgroundColor: '#f5f5f5',
            },
            '&:disabled': {
              backgroundColor: '#e0e0e0',
            },
          }}
        >
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </Navbar>
      <ContentWrapper>
        <Sidebar>
          <SidebarHeader>
            <SidebarTitle>Slice List</SidebarTitle>
          </SidebarHeader>
          <ImageListContainer>
            {loadingAnnotations || loadingALData ? (
              <ProgressContainer>
                <CircularProgress size={24} />
                <ProgressText>Loading annotations...</ProgressText>
              </ProgressContainer>
            ) : filteredSlices.length > 0 ? (
              <ImageList>
                {filteredSlices.map((sliceIndex) => (
                  <ImageListItem
                    key={sliceIndex}
                    isSelected={selectedSlice === sliceIndex}
                    onClick={() => setSelectedSlice(sliceIndex)}
                  >
                    <SliceInfo>
                      <ImageName isSelected={selectedSlice === sliceIndex}>
                        Slice {sliceIndex}
                      </ImageName>
                    </SliceInfo>
                  </ImageListItem>
                ))}
              </ImageList>
            ) : totalSlices > 0 ? (
              // Fallback to showing all slices if no filtered slices available
              <ImageList>
                {Array.from({ length: totalSlices }, (_, index) => (
                  <ImageListItem
                    key={index}
                    isSelected={selectedSlice === index}
                    onClick={() => setSelectedSlice(index)}
                  >
                    <ImageName isSelected={selectedSlice === index}>
                      Slice {index}
                    </ImageName>
                  </ImageListItem>
                ))}
              </ImageList>
            ) : (
              <WelcomeContainer>
                <WelcomeText>No slices available</WelcomeText>
              </WelcomeContainer>
            )}
          </ImageListContainer>
        </Sidebar>
        <MainContent>
          {loading && (
            <ProgressContainer>
              <ProgressWrapper>
                <ProgressText>
                  Loading NIFTI file... {downloadProgress}%
                  {downloadTotal > 0 && (
                    <span style={{ display: 'block', fontSize: '12px', marginTop: '4px', color: '#666' }}>
                      {formatBytes(downloadLoaded)} / {formatBytes(downloadTotal)}
                    </span>
                  )}
                </ProgressText>
                <ProgressBarContainer>
                  <ProgressBar progress={downloadProgress} />
                </ProgressBarContainer>
              </ProgressWrapper>
            </ProgressContainer>
          )}
          {error && (
            <div style={{ 
              color: 'red',
              padding: '20px',
              textAlign: 'center' 
            }}>
              <strong>Error:</strong> {error}
            </div>
          )}
          {!loading && !niftiFile && !error && (
            <WelcomeContainer>
              <WelcomeIcon />
              <WelcomeTitle>Welcome to AL Correction</WelcomeTitle>
              <WelcomeText>
                Select a slice from the list on the left to start viewing and correcting auto-labelled annotations.
              </WelcomeText>
            </WelcomeContainer>
          )}
          {!loading && niftiFile && (
            <NiftiViewerContainer>
              <NiftiFileReader 
                initialNiftiData={niftiFile} 
                projectId={projectId || ''} 
                imageId={decodedFileName} 
                labelsFromContext={labels} 
                preLoadedAnnotations={preLoadedAnnotations}
                onSave={handleSave}
                hideSaveButton={true}
                saveTrigger={saveTrigger}
                externalSliceIndex={selectedSlice !== null ? selectedSlice : undefined}
                showSamToggle={true}
              />
            </NiftiViewerContainer>
          )}
        </MainContent>
      </ContentWrapper>
    </PageContainer>
  );
}

export default ALCorrectionDetailPage;

